import { useState, useEffect, useRef } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calculator, AlertTriangle, Download, FileText, FileDown, Plus, Trash2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ProjectSupportDialog from "@/components/ProjectSupportDialog";
import { calculateSundryTotal } from "@/components/SundryItemsManager";
import QuoteTargetSelector from "@/components/QuoteTargetSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FullHeightTurnstiles = ({ onBack, selectedQuote, backLabel = "Back to Pedestrian Entrance Control" }) => {
  const navigate = useNavigate();
  
  // Form state
  const [projectName, setProjectName] = useState("");
  const [quoteReference, setQuoteReference] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [finishType, setFinishType] = useState("");
  const [turnstileType, setTurnstileType] = useState("");
  const [armConfig, setArmConfig] = useState("");
  const [coverType, setCoverType] = useState("");
  const [priceModifier, setPriceModifier] = useState("");
  const [currentConfigId, setCurrentConfigId] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [quoteTarget, setQuoteTarget] = useState(""); // "distributor" or "installer"
  const [installerOnlyCapture, setInstallerOnlyCapture] = useState(false);
  
  // Result state
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Project Support state
  const [showProjectSupport, setShowProjectSupport] = useState(false);
  const [supportDeclined, setSupportDeclined] = useState(false);
  
  // Sundry Items
  const [sundryItems, setSundryItems] = useState([]);
  const [showSundryDialog, setShowSundryDialog] = useState(false);
  const [editingSundryIndex, setEditingSundryIndex] = useState(null);
  const [sundryForm, setSundryForm] = useState({
    sku: '',
    description: '',
    quantity: 1,
    unit_price: 0
  });
  
  // Quote box ref for download
  const quoteBoxRef = useRef(null);

  // Sundry Items Functions
  const handleAddSundryItem = () => {
    setSundryForm({ sku: '', description: '', quantity: 1, unit_price: 0 });
    setEditingSundryIndex(null);
    setShowSundryDialog(true);
  };

  const handleEditSundryItem = (index) => {
    setSundryForm(sundryItems[index]);
    setEditingSundryIndex(index);
    setShowSundryDialog(true);
  };

  const handleSaveSundryItem = () => {
    if (!sundryForm.description || sundryForm.quantity <= 0) {
      setError('Please enter a description and valid quantity');
      return;
    }
    
    const newItem = {
      ...sundryForm,
      quantity: parseFloat(sundryForm.quantity) || 1,
      unit_price: parseFloat(sundryForm.unit_price) || 0,
      line_total: (parseFloat(sundryForm.quantity) || 1) * (parseFloat(sundryForm.unit_price) || 0)
    };
    
    if (editingSundryIndex !== null) {
      const updated = [...sundryItems];
      updated[editingSundryIndex] = newItem;
      setSundryItems(updated);
    } else {
      setSundryItems([...sundryItems, newItem]);
    }
    
    setShowSundryDialog(false);
    setSundryForm({ sku: '', description: '', quantity: 1, unit_price: 0 });
    setEditingSundryIndex(null);
  };

  const handleDeleteSundryItem = (index) => {
    if (window.confirm('Remove this sundry item?')) {
      setSundryItems(sundryItems.filter((_, i) => i !== index));
    }
  };

  const handleDownloadQuoteImage = async (installerOnly = false) => {
    if (!quoteBoxRef.current) return;
    try {
      if (installerOnly) {
        setInstallerOnlyCapture(true);
        await new Promise(r => setTimeout(r, 250));
      }
      const canvas = await html2canvas(quoteBoxRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false
      });
      const suffix = installerOnly ? 'Installer' : (isDistributorUser ? 'Distributor' : '');
      const link = document.createElement('a');
      link.download = `${projectName || 'FullHeight'}${suffix ? '_' + suffix : ''}_Quote.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error downloading quote:', err);
      setError('Failed to download quote image');
    } finally {
      if (installerOnly) setInstallerOnlyCapture(false);
    }
  };

  // Fetch user role on mount
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserRole(response.data.role);
      } catch (err) {
        console.error('Error fetching user role:', err);
      }
    };
    fetchUserRole();
  }, []);

  // BOM pricing visibility based on quoteTarget for admin users
  const isAdmin = userRole === 'admin' || userRole === 'zkteco_admin' || userRole === 'zkteco';
  const isDistributorUser = userRole === 'distributor' || userRole === 'distributor_admin';
  const showBomDistributorPrice = !installerOnlyCapture && ((isAdmin && quoteTarget === 'distributor') || isDistributorUser);
  const showBomInstallerPrice = isAdmin ? (quoteTarget === 'distributor' || quoteTarget === 'installer') : (isDistributorUser || userRole === 'installer');
  const showBomEndUserPrice = isAdmin && quoteTarget === 'end_user';

  // Load selected quote if provided
  useEffect(() => {
    if (selectedQuote && selectedQuote.configurator_type === 'full_height_turnstiles') {
      setProjectName(selectedQuote.project_name || "");
      setQuoteReference(selectedQuote.quote_reference || "");
      setQuantity(String(selectedQuote.quantity || 1));
      setFinishType(selectedQuote.finish_type || "");
      setTurnstileType(selectedQuote.turnstile_type || "");
      setArmConfig(selectedQuote.arm_config || "");
      setCoverType(selectedQuote.cover_type || "");
      setPriceModifier(selectedQuote.price_modifier ? String(selectedQuote.price_modifier) : "");
      setCurrentConfigId(selectedQuote.id);
      
      setSuccess("✓ Quote loaded successfully! Click Calculate to recalculate or modify the configuration.");
      setTimeout(() => setSuccess(""), 5000);
    }
  }, [selectedQuote]);

  const handleCalculate = async () => {
    setError("");
    setSuccess("");
    
    // Validation
    if (!projectName.trim()) {
      setError("Project name is required");
      return;
    }
    
    if (!quantity || parseInt(quantity) < 1) {
      setError("Please enter a valid quantity");
      return;
    }
    
    if (!finishType || !turnstileType || !armConfig || !coverType) {
      setError("Please complete all configuration steps");
      return;
    }
    
    if (isAdmin && !quoteTarget) {
      setError("Please select who this quote is for (Distributor, Installer, or End User)");
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/configure-full-height-turnstiles`,
        {
          project_name: projectName,
          quote_reference: quoteReference,
          quantity: parseInt(quantity),
          finish_type: finishType,
          turnstile_type: turnstileType,
          arm_config: armConfig,
          cover_type: coverType,
          price_modifier: priceModifier ? parseFloat(priceModifier) : 0,
          config_id: currentConfigId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResult(response.data);
      setCurrentConfigId(response.data.configuration_id);
      setSuccess("✓ Quote calculated and saved successfully!");
      
      // Check if installer price >= £25,000 for project support (only show once per quote)
      const installerTotal = response.data.final_installer_price || 0;
      if (installerTotal >= 25000 && !supportDeclined) {
        setShowProjectSupport(true);
      }
      
      // Scroll to results
      setTimeout(() => {
        document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err) {
      setError(err.response?.data?.detail || "Configuration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleNewQuote = () => {
    if (projectName || quantity !== "1" || finishType || turnstileType || armConfig || coverType) {
      if (!window.confirm("Start a new quote? Any unsaved changes will be lost.")) {
        return;
      }
    }
    
    setProjectName("");
    setQuoteReference("");
    setQuantity("1");
    setFinishType("");
    setTurnstileType("");
    setArmConfig("");
    setCoverType("");
    setCurrentConfigId(null);
    setResult(null);
    setError("");
    setSuccess("");
  };

  const finishOptions = [
    { value: 'fully_painted', label: 'A. Fully Painted', description: 'Complete painted finish' },
    { value: 'body_painted', label: 'B. Body Painted & Rotor Stainless Steel', description: 'Mixed finish' },
    { value: 'all_galvanised', label: 'C. All Galvanised', description: 'Full galvanised finish' },
    { value: 'galvanised_painted', label: 'D. Galvanised and Painted', description: 'Galvanised with paint' },
    { value: 'stainless_steel', label: 'E. Stainless Steel (SUS304)', description: 'Premium stainless finish' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Button 
          onClick={onBack || (() => navigate('/dashboard'))}
          variant="outline" 
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {backLabel}
        </Button>

        <Card>
          <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
            <CardTitle className="text-3xl">Full Height Turnstiles Configurator</CardTitle>
            <CardDescription className="text-orange-100">
              Configure your Full Height Turnstile solution
            </CardDescription>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            {/* Project Details */}
            <Card className="bg-slate-50">
              <CardHeader>
                <CardTitle>Project Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="projectName">
                    Project Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="projectName"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="Enter project name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="quoteReference">Quote Reference (Optional)</Label>
                  <Input
                    id="quoteReference"
                    value={quoteReference}
                    onChange={(e) => setQuoteReference(e.target.value)}
                    placeholder="Enter quote reference"
                  />
                </div>
                
                {/* Price Modifier for Distributors and Installers */}
                {(userRole === 'distributor' || userRole === 'admin' || userRole === 'distributor_admin') && (
                  <div>
                    <Label htmlFor="priceModifier">Installer Discount (%)</Label>
                    <Input
                      id="priceModifier"
                      type="number"
                      step="0.1"
                      min="0"
                      value={priceModifier}
                      onChange={(e) => setPriceModifier(e.target.value)}
                      placeholder="e.g., 5 for 5% discount"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Enter discount percentage to reduce installer price
                    </p>
                  </div>
                )}
                
                {/* Quote Target Selector - ZKTeco Staff Only */}
                <QuoteTargetSelector 
                  quoteTarget={quoteTarget} 
                  setQuoteTarget={setQuoteTarget} 
                  userRole={userRole} 
                />
              </CardContent>
            </Card>

            {/* Question 1: Quantity */}
            <Card className="bg-slate-50">
              <CardHeader>
                <CardTitle>Question 1: Number of Full Height Turnstiles</CardTitle>
              </CardHeader>
              <CardContent>
                <Label htmlFor="quantity">Quantity <span className="text-red-500">*</span></Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="max-w-xs"
                />
              </CardContent>
            </Card>

            {/* Question 2: Finish */}
            <Card className="bg-slate-50">
              <CardHeader>
                <CardTitle>Question 2: Finish Type</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={finishType} onValueChange={setFinishType}>
                  <div className="space-y-3">
                    {finishOptions.map((option) => (
                      <div key={option.value} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-white cursor-pointer transition-colors">
                        <RadioGroupItem value={option.value} id={option.value} />
                        <Label htmlFor={option.value} className="flex-1 cursor-pointer">
                          <div className="font-semibold">{option.label}</div>
                          <div className="text-sm text-slate-600">{option.description}</div>
                        </Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>

                {finishType === 'stainless_steel' && (
                  <Alert className="mt-4 bg-amber-50 border-amber-300">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-900">
                      <strong>Note:</strong> Stainless Steel turnstiles use SUS304 grade. If SUS316 grade is required, 
                      please contact ZKTeco UK directly.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Question 3: Single or Double */}
            <Card className="bg-slate-50">
              <CardHeader>
                <CardTitle>Question 3: Single or Double Turnstile</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={turnstileType} onValueChange={setTurnstileType}>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white cursor-pointer transition-colors">
                      <RadioGroupItem value="single" id="single" />
                      <Label htmlFor="single" className="flex-1 cursor-pointer">
                        <div className="font-semibold">A. Single Turnstile</div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white cursor-pointer transition-colors">
                      <RadioGroupItem value="double" id="double" />
                      <Label htmlFor="double" className="flex-1 cursor-pointer">
                        <div className="font-semibold">B. Double Turnstile</div>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Question 4: Arms */}
            <Card className="bg-slate-50">
              <CardHeader>
                <CardTitle>Question 4: Arm Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={armConfig} onValueChange={setArmConfig}>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white cursor-pointer transition-colors">
                      <RadioGroupItem value="3" id="3arms" />
                      <Label htmlFor="3arms" className="flex-1 cursor-pointer">
                        <div className="font-semibold">A. 3 Arms with 120° Passage</div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white cursor-pointer transition-colors">
                      <RadioGroupItem value="4" id="4arms" />
                      <Label htmlFor="4arms" className="flex-1 cursor-pointer">
                        <div className="font-semibold">B. 4 Arms with 90° Passage</div>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Question 5: Budget or Covered */}
            <Card className="bg-slate-50">
              <CardHeader>
                <CardTitle>Question 5: Roof Coverage</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={coverType} onValueChange={setCoverType}>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white cursor-pointer transition-colors">
                      <RadioGroupItem value="budget" id="budget" />
                      <Label htmlFor="budget" className="flex-1 cursor-pointer">
                        <div className="font-semibold">A. Budget (No Roof)</div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white cursor-pointer transition-colors">
                      <RadioGroupItem value="covered" id="covered" />
                      <Label htmlFor="covered" className="flex-1 cursor-pointer">
                        <div className="font-semibold">B. Covered (With Roof)</div>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Error/Success Messages */}
            {error && (
              <Alert className="bg-red-50 border-red-300">
                <AlertDescription className="text-red-900">{error}</AlertDescription>
              </Alert>
            )}
            {success && (
              <Alert className="bg-green-50 border-green-300">
                <AlertDescription className="text-green-900">{success}</AlertDescription>
              </Alert>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button 
                onClick={handleCalculate} 
                disabled={loading}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                <Calculator className="w-4 h-4 mr-2" />
                {loading ? "Calculating..." : "Calculate & Save Quote"}
              </Button>
              <Button 
                onClick={handleNewQuote}
                variant="outline"
              >
                New Quote
              </Button>
            </div>

            {/* Results Section - BOM Table */}
            {result && (
              <div id="results" className="space-y-4 pt-6 border-t" ref={quoteBoxRef}>
                <Card>
                  <CardHeader className="bg-slate-900 text-white flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Bill of Materials
                      </CardTitle>
                      <CardDescription className="text-slate-300">
                        {projectName || 'Full Height Turnstiles Quote'}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2" data-html2canvas-ignore="true">
                      {isDistributorUser ? (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            data-testid="download-distributor-quote-btn"
                            onClick={() => handleDownloadQuoteImage(false)}
                            className="bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-2"
                          >
                            <Download className="h-4 w-4" />
                            Distributor Quote
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            data-testid="download-installer-quote-btn"
                            onClick={() => handleDownloadQuoteImage(true)}
                            className="bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-2"
                          >
                            <Download className="h-4 w-4" />
                            Installer Quote
                          </Button>
                        </>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDownloadQuoteImage(false)}
                          className="bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Download Quote
                        </Button>
                      )}
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={handleAddSundryItem}
                        className="bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add Sundry Line
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-2 font-semibold text-slate-700">Category</th>
                            <th className="text-left py-3 px-2 font-semibold text-slate-700">SKU</th>
                            <th className="text-left py-3 px-2 font-semibold text-slate-700">Description</th>
                            <th className="text-right py-3 px-2 font-semibold text-slate-700">Qty</th>
                            {showBomDistributorPrice && (
                              <>
                                <th className="text-right py-3 px-2 font-semibold text-purple-700">Distributor Price</th>
                                <th className="text-right py-3 px-2 font-semibold text-purple-700">Dist. Total</th>
                              </>
                            )}
                            {showBomInstallerPrice && (
                              <>
                                <th className="text-right py-3 px-2 font-semibold text-green-700">{userRole === 'installer' ? 'Unit Price' : 'Installer Price'}</th>
                                <th className="text-right py-3 px-2 font-semibold text-green-700">{userRole === 'installer' ? 'Line Total' : 'Inst. Total'}</th>
                              </>
                            )}
                            {showBomEndUserPrice && (
                              <>
                                <th className="text-right py-3 px-2 font-semibold text-orange-700">End User Price</th>
                                <th className="text-right py-3 px-2 font-semibold text-orange-700">EU Total</th>
                              </>
                            )}
                            <th className="text-center py-3 px-2 font-semibold text-slate-700 print:hidden"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(result.combined_bom || []).map((item, index) => (
                            <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 px-2 text-slate-600">{item.category}</td>
                              <td className="py-3 px-2">
                                {item.product_url ? (
                                  <a
                                    href={
                                      item.product_url.startsWith('http') 
                                        ? item.product_url 
                                        : `${BACKEND_URL}${item.product_url}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline font-medium cursor-pointer"
                                    title="View datasheet"
                                  >
                                    {item.sku}
                                  </a>
                                ) : (
                                  <span className="text-slate-900 font-medium">{item.sku}</span>
                                )}
                              </td>
                              <td className="py-3 px-2 text-slate-600">{item.description}</td>
                              <td className="py-3 px-2 text-right text-slate-900">{item.quantity}</td>
                              {showBomDistributorPrice && (
                                <>
                                  <td className="py-3 px-2 text-right text-purple-700">£{item.unit_price.toFixed(2)}</td>
                                  <td className="py-3 px-2 text-right text-purple-700 font-medium">£{item.line_total.toFixed(2)}</td>
                                </>
                              )}
                              {showBomInstallerPrice && (
                                <>
                                  <td className="py-3 px-2 text-right text-green-700 font-medium">£{item.installer_price.toFixed(2)}</td>
                                  <td className="py-3 px-2 text-right text-green-700 font-semibold">£{item.installer_total.toFixed(2)}</td>
                                </>
                              )}
                              {showBomEndUserPrice && (
                                <>
                                  <td className="py-3 px-2 text-right text-orange-700 font-medium">£{(item.end_user_price || 0).toFixed(2)}</td>
                                  <td className="py-3 px-2 text-right text-orange-700 font-semibold">£{(item.end_user_total || 0).toFixed(2)}</td>
                                </>
                              )}
                              <td className="print:hidden"></td>
                            </tr>
                          ))}
                          {/* Sundry Items in B.O.M */}
                          {sundryItems.map((item, index) => (
                            <tr key={`sundry-${index}`} className="border-b border-amber-200 bg-amber-50 hover:bg-amber-100">
                              <td className="py-3 px-2 text-amber-700 font-medium">Sundry</td>
                              <td className="py-3 px-2 text-amber-800 font-mono text-xs">{item.sku || '-'}</td>
                              <td className="py-3 px-2 text-amber-800">{item.description}</td>
                              <td className="py-3 px-2 text-right text-amber-900">{item.quantity}</td>
                              <td className="py-3 px-2 text-right text-amber-900">£{item.unit_price.toFixed(2)}</td>
                              <td className="py-3 px-2 text-right text-amber-900 font-medium">£{item.line_total.toFixed(2)}</td>
                              {(result.user_role === 'distributor' || result.user_role === 'admin') && (
                                <>
                                  <td className="py-3 px-2 text-right text-amber-700">£{item.unit_price.toFixed(2)}</td>
                                  <td className="py-3 px-2 text-right text-amber-700 font-semibold">£{item.line_total.toFixed(2)}</td>
                                </>
                              )}
                              <td className="py-3 px-2 text-center print:hidden">
                                <div className="flex justify-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-amber-700 hover:text-amber-900"
                                    onClick={() => handleEditSundryItem(index)}
                                  >
                                    <FileText className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                    onClick={() => handleDeleteSundryItem(index)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          {(showBomDistributorPrice || showBomInstallerPrice || showBomEndUserPrice) ? (
                            <>
                              <tr className="border-t-2 border-slate-300 bg-slate-50">
                                <td colSpan="4" className="py-3 px-2 text-right font-semibold">Subtotals:</td>
                                {showBomDistributorPrice && (
                                  <>
                                    <td className="py-3 px-2 text-right font-semibold text-purple-700">Distributor:</td>
                                    <td className="py-3 px-2 text-right font-bold text-purple-700 text-lg">£{result.trade_subtotal.toFixed(2)}</td>
                                  </>
                                )}
                                {showBomInstallerPrice && (
                                  <>
                                    <td className="py-3 px-2 text-right font-semibold text-green-700">{userRole === 'installer' ? 'Subtotal:' : 'Installer:'}</td>
                                    <td className="py-3 px-2 text-right font-bold text-green-700 text-lg">£{result.installer_subtotal?.toFixed(2)}</td>
                                  </>
                                )}
                                {showBomEndUserPrice && (
                                  <>
                                    <td className="py-3 px-2 text-right font-semibold text-orange-700">End User:</td>
                                    <td className="py-3 px-2 text-right font-bold text-orange-700 text-lg">
                                      £{(result.combined_bom || []).reduce((sum, item) => sum + (item.end_user_total || 0), 0).toFixed(2)}
                                    </td>
                                  </>
                                )}
                                <td className="print:hidden"></td>
                              </tr>
                              <tr className="bg-slate-50">
                                <td colSpan="4" className="py-3 px-2 text-right font-medium text-slate-700">Delivery ({result.quantity} unit{result.quantity > 1 ? 's' : ''}):</td>
                                {showBomDistributorPrice && (
                                  <>
                                    <td className="py-3 px-2"></td>
                                    <td className="py-3 px-2 text-right font-semibold text-purple-700">£{result.delivery_cost.toFixed(2)}</td>
                                  </>
                                )}
                                {showBomInstallerPrice && (
                                  <>
                                    <td className="py-3 px-2"></td>
                                    <td className="py-3 px-2 text-right font-semibold text-green-700">£{result.delivery_cost_installer?.toFixed(2)}</td>
                                  </>
                                )}
                                {showBomEndUserPrice && (
                                  <>
                                    <td className="py-3 px-2"></td>
                                    <td className="py-3 px-2 text-right font-semibold text-orange-700">£{result.delivery_cost_installer?.toFixed(2)}</td>
                                  </>
                                )}
                                <td className="print:hidden"></td>
                              </tr>
                              <tr>
                                <td colSpan={4 + (showBomDistributorPrice ? 2 : 0) + (showBomInstallerPrice ? 2 : 0) + (showBomEndUserPrice ? 2 : 0) + 1} className="py-1 px-2 text-right text-xs text-slate-500 italic">
                                  (Delivery surcharges for multiple units may vary - please check with your ZKTeco Rep.)
                                </td>
                              </tr>
                              {result.price_modifier > 0 && showBomInstallerPrice && (
                                <tr className="bg-blue-50">
                                  <td colSpan={4 + (showBomDistributorPrice ? 2 : 0)} className="py-3 px-2 text-right font-medium text-blue-800">
                                    Installer Discount ({result.price_modifier}%):
                                  </td>
                                  <td className="py-3 px-2"></td>
                                  <td className="py-3 px-2 text-right font-semibold text-blue-900">
                                    -£{result.modifier_amount.toFixed(2)}
                                  </td>
                                  <td className="print:hidden"></td>
                                </tr>
                              )}
                              <tr className="border-t-2 border-slate-400 bg-slate-900 text-white">
                                <td colSpan="4" className="py-4 px-2 text-right font-bold text-lg">Final Totals:</td>
                                {showBomDistributorPrice && (
                                  <>
                                    <td className="py-4 px-2 text-right font-bold text-purple-300">Distributor:</td>
                                    <td className="py-4 px-2 text-right font-bold text-xl text-purple-300">£{result.subtotal_with_delivery.toFixed(2)}</td>
                                  </>
                                )}
                                {showBomInstallerPrice && (
                                  <>
                                    <td className="py-4 px-2 text-right font-bold text-green-300">{userRole === 'installer' ? 'Your Total:' : 'Installer:'}</td>
                                    <td className="py-4 px-2 text-right font-bold text-2xl text-green-400">£{result.final_installer_price?.toFixed(2)}</td>
                                  </>
                                )}
                                {showBomEndUserPrice && (
                                  <>
                                    <td className="py-4 px-2 text-right font-bold text-orange-300">End User:</td>
                                    <td className="py-4 px-2 text-right font-bold text-2xl text-orange-400">
                                      £{((result.combined_bom || []).reduce((sum, item) => sum + (item.end_user_total || 0), 0) + (result.delivery_cost_installer || 0)).toFixed(2)}
                                    </td>
                                  </>
                                )}
                                <td className="print:hidden"></td>
                              </tr>
                              {sundryItems.length > 0 && (
                                <>
                                  <tr className="bg-amber-50">
                                    <td colSpan={4 + (showBomDistributorPrice ? 2 : 0) + (showBomInstallerPrice ? 1 : 0) + (showBomEndUserPrice ? 1 : 0)} className="py-3 px-2 text-right font-semibold text-amber-800">Sundry Items Total:</td>
                                    <td className="py-3 px-2 text-right font-bold text-amber-800">£{calculateSundryTotal(sundryItems).toFixed(2)}</td>
                                    <td className="print:hidden"></td>
                                  </tr>
                                  <tr className="bg-green-100 border-t-2 border-green-400">
                                    <td colSpan={4 + (showBomDistributorPrice ? 2 : 0) + (showBomInstallerPrice ? 1 : 0) + (showBomEndUserPrice ? 1 : 0)} className="py-3 px-2 text-right font-bold text-green-900">Grand Total (incl. Sundry):</td>
                                    <td className="py-3 px-2 text-right font-bold text-green-900 text-xl">
                                      £{showBomEndUserPrice
                                        ? ((result.combined_bom || []).reduce((sum, item) => sum + (item.end_user_total || 0), 0) + (result.delivery_cost_installer || 0) + calculateSundryTotal(sundryItems)).toFixed(2)
                                        : (result.final_installer_price + calculateSundryTotal(sundryItems)).toFixed(2)}
                                    </td>
                                    <td className="print:hidden"></td>
                                  </tr>
                                </>
                              )}
                              {showBomDistributorPrice && (
                                <tr className="bg-green-50">
                                  <td colSpan={4 + (showBomDistributorPrice ? 2 : 0) + (showBomInstallerPrice ? 1 : 0)} className="py-3 px-2 text-right font-semibold text-green-800">Your Margin:</td>
                                  <td className="py-3 px-2 text-right font-bold text-green-800 text-lg">
                                    £{result.distributor_margin?.toFixed(2)}
                                    <span className="text-sm ml-2">
                                      ({((result.distributor_margin / result.final_installer_price) * 100).toFixed(1)}%)
                                    </span>
                                  </td>
                                  <td className="print:hidden"></td>
                                </tr>
                              )}
                            </>
                          ) : (
                            <>
                              <tr className="border-t-2 border-slate-300 bg-slate-50">
                                <td colSpan="5" className="py-3 px-2 text-right font-semibold text-slate-900">Equipment Subtotal:</td>
                                <td className="py-3 px-2 text-right font-bold text-slate-900 text-lg">£{result.trade_subtotal.toFixed(2)}</td>
                              </tr>
                              <tr className="bg-slate-50">
                                <td colSpan="5" className="py-3 px-2 text-right font-medium text-slate-700">Delivery:</td>
                                <td className="py-3 px-2 text-right font-semibold text-slate-900">£{result.delivery_cost.toFixed(2)}</td>
                              </tr>
                              <tr>
                                <td colSpan="6" className="py-1 px-2 text-right text-xs text-slate-500 italic">
                                  (Delivery surcharges for multiple units may vary - please check with your ZKTeco Rep.)
                                </td>
                              </tr>
                              <tr className="border-t-2 border-slate-400 bg-slate-900 text-white">
                                <td colSpan="5" className="py-4 px-2 text-right font-bold text-lg">Total:</td>
                                <td className="py-4 px-2 text-right font-bold text-2xl">£{result.final_total.toFixed(2)}</td>
                              </tr>
                              {sundryItems.length > 0 && (
                                <>
                                  <tr className="bg-amber-50">
                                    <td colSpan="5" className="py-3 px-2 text-right font-semibold text-amber-800">Sundry Items Total:</td>
                                    <td className="py-3 px-2 text-right font-bold text-amber-800">£{calculateSundryTotal(sundryItems).toFixed(2)}</td>
                                  </tr>
                                  <tr className="bg-green-100 border-t-2 border-green-400">
                                    <td colSpan="5" className="py-3 px-2 text-right font-bold text-green-900">Grand Total (incl. Sundry):</td>
                                    <td className="py-3 px-2 text-right font-bold text-green-900 text-xl">£{(result.final_total + calculateSundryTotal(sundryItems)).toFixed(2)}</td>
                                  </tr>
                                </>
                              )}
                            </>
                          )}
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Project Support Dialog */}
        <ProjectSupportDialog
          open={showProjectSupport}
          onOpenChange={(open) => {
            setShowProjectSupport(open);
            // If user closes dialog without submitting, mark as declined
            if (!open) {
              setSupportDeclined(true);
            }
          }}
          quoteData={{
            project_name: projectName,
            quote_reference: quoteReference,
            installer_total: result?.final_installer_price
          }}
          configuratorType="full_height_turnstiles"
        />

        {/* Sundry Item Dialog */}
        <Dialog open={showSundryDialog} onOpenChange={setShowSundryDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingSundryIndex !== null ? 'Edit Sundry Item' : 'Add Sundry Item'}</DialogTitle>
              <DialogDescription>
                Add custom line items such as labour, cabling, commissioning, travel, etc.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="sundry-sku">SKU (optional)</Label>
                <Input
                  id="sundry-sku"
                  placeholder="e.g. LABOUR-001"
                  value={sundryForm.sku}
                  onChange={(e) => setSundryForm({...sundryForm, sku: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sundry-description">Description *</Label>
                <Input
                  id="sundry-description"
                  placeholder="e.g. Installation labour, Cabling, Travel"
                  value={sundryForm.description}
                  onChange={(e) => setSundryForm({...sundryForm, description: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sundry-quantity">Quantity *</Label>
                  <Input
                    id="sundry-quantity"
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="1"
                    value={sundryForm.quantity}
                    onChange={(e) => setSundryForm({...sundryForm, quantity: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sundry-price">Unit Price (£) *</Label>
                  <Input
                    id="sundry-price"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={sundryForm.unit_price}
                    onChange={(e) => setSundryForm({...sundryForm, unit_price: e.target.value})}
                  />
                </div>
              </div>
              {sundryForm.quantity && sundryForm.unit_price && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Line Total:</span>
                    <span className="font-semibold">
                      £{((parseFloat(sundryForm.quantity) || 0) * (parseFloat(sundryForm.unit_price) || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSundryDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveSundryItem}>
                {editingSundryIndex !== null ? 'Update' : 'Add Item'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default FullHeightTurnstiles;
